import { ErrorBase } from "algoliasearch/lite";
import React, { useEffect, useState } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { arrayHasContent } from "@utils/array";
import { AlgoliaRecommendZones, ProductSliderSources } from "@utils/enums";
import { isDefined } from "@utils/helpers";
import { useSearchClient } from "@utils/hooks/algolia/useSearchClient.hook";
import { useProductCartContext } from "@utils/hooks/cart.hook";
import { getBreakPointLayoutSettings } from "@utils/swiper";

import { getRecommendations } from "@services/algolia/recommend/algoliaRecommend.service";
import { ParamsAlgolia } from "@services/algolia/types/paramsAlgolia.types";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    CartItemModel,
    CartModel,
} from "@redux/modules/api/data/cart/@types/cart.model";

import { ProductSlider } from "@components/Product/ProductSlider/productSlider.component";

type ProductSliderProps = {
    title: string;
    showButton: boolean;
    showCategoryLink: boolean;
    amountOfFeatures: number;
    useInsidePadding: boolean;
    breakpointslayoutsettings: any;
    zoneId: string;
    prefetchedData?: Array<ProductTileModel>;
    ssrLayoutClasses?: string;
    productSku?: string;
    lazyLoad: boolean;
    paramsAlgolia: ParamsAlgolia;
};

type Props = ProductSliderProps & {};

const getCartSkuList = (cart?: CartModel | null) => {
    const isRecommendationsAllowed = ({
        articleConfiguration,
        isAdditionalService,
        giftCardArticle,
    }: CartItemModel) =>
        !articleConfiguration && !isAdditionalService && !giftCardArticle;

    return (
        cart?.items.filter(isRecommendationsAllowed).map(item => item.sku) ?? []
    );
};

const AlgoliaRecommendProductslider = ({
    title,
    showButton,
    amountOfFeatures,
    showCategoryLink,
    breakpointslayoutsettings,
    useInsidePadding,
    zoneId,
    prefetchedData,
    ssrLayoutClasses,
    productSku,
    lazyLoad,
    paramsAlgolia,
}: Props) => {
    const dispatch = useThunkDispatch();

    const [products, setProducts] = useState<Array<ProductTileModel>>(
        prefetchedData || [],
    );
    const { cart } = useProductCartContext();
    const searchClient = useSearchClient();

    const updateRecommendations = (skus: Array<string | undefined>) => {
        if (!isDefined(searchClient)) return;

        const validSkus = skus.filter(isDefined);
        if (!arrayHasContent(validSkus)) return;

        getRecommendations(searchClient, dispatch)(validSkus, paramsAlgolia)
            .then(setProducts)
            .catch((error: ErrorBase) =>
                console.warn("Algolia: Unable to fetch recommendations", error),
            );
    };

    useEffect(() => {
        if (zoneId !== AlgoliaRecommendZones.addToCart) return;

        updateRecommendations([productSku]);
    }, [searchClient]);

    useEffect(() => {
        if (zoneId !== AlgoliaRecommendZones.shoppingCart) return;

        const skus = getCartSkuList(cart);
        skus.length > 0 ? updateRecommendations(skus) : setProducts([]);
    }, [cart?.items.length, searchClient]);

    useEffect(() => {
        if (!isDefined(prefetchedData)) return;

        setProducts(prefetchedData);
    }, [prefetchedData]);

    if (!arrayHasContent(products)) return null;

    return (
        <>
            <ProductSlider
                lazyLoad={lazyLoad}
                title={title}
                products={products}
                productsource={ProductSliderSources.algoliaRecommend}
                showButton={showButton}
                amountOfFeatures={amountOfFeatures}
                useInsidePadding={useInsidePadding}
                showCategoryLink={showCategoryLink}
                breakpointslayoutsettings={getBreakPointLayoutSettings(
                    breakpointslayoutsettings,
                )}
                ssrLayoutClasses={ssrLayoutClasses}
                analyticsContext={{
                    list: "product-slider-algolia-recommend",
                    listTitle: title,
                }}
            />
        </>
    );
};

export { AlgoliaRecommendProductslider };
